// PRC-019: progreso de «Primeros pasos» guardado en el servidor por usuario.
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../config/axiosInstance';
import { SYSTEM_FIELDS } from '../../config/systemFields';

export interface PricingOnboarding {
  welcome_seen: boolean;
  account_created_at: string | null;
  steps: { account: boolean; sales: boolean; quality: boolean; overview: boolean; optimization: boolean };
  done: number;
  total: number;
}

export type OnboardingEvent = 'welcome' | 'quality' | 'overview';

export const ONBOARDING_QUERY_KEY = ['pricing-onboarding'];

export const usePricingOnboarding = (enabled = true) =>
  useQuery({
    queryKey: ONBOARDING_QUERY_KEY,
    queryFn: async () => (await axiosInstance.get<PricingOnboarding>('/pricing-onboarding')).data,
    enabled,
    staleTime: 60_000,
    retry: false,
  });

export async function markOnboardingEvent(event: OnboardingEvent): Promise<void> {
  await axiosInstance.post('/pricing-onboarding/events', { event });
}

// Marca un paso una sola vez cuando se cumple la condición (p. ej. Overview con datos).
export function useMarkOnboardingEvent(event: OnboardingEvent, when = true): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!when) return;
    markOnboardingEvent(event)
      .then(() => queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY }))
      .catch(() => undefined);
  }, [event, when, queryClient]);
}

// Columnas mínimas reales: las obligatorias de la pantalla de carga (config/systemFields.ts).
export const UPLOAD_FILES: Array<{ type: 'customers' | 'products' | 'transactions'; label: string }> = [
  { type: 'transactions', label: 'Ventas' },
  { type: 'customers', label: 'Clientes' },
  { type: 'products', label: 'Productos' },
];

export const requiredColumns = (type: 'customers' | 'products' | 'transactions') =>
  SYSTEM_FIELDS[type].filter((f) => f.required);

export function downloadTemplate(type: 'customers' | 'products' | 'transactions'): void {
  const header = requiredColumns(type).map((f) => f.key).join(';');
  const blob = new Blob([`${header}\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `plantilla_${type}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
